/**
 * Protocol-wide constants. Changing any of these requires a fresh trusted setup
 * ceremony and a new mainnet deployment.
 */
/**
 * Height of the deposit Merkle tree. 2^20 ≈ 1M deposits per pool.
 *
 * The merkle tree is maintained off-chain. The contract's deposit handler
 * accepts a `newRoot` field computed by the depositor and stores it in the
 * known-roots dict; the withdraw SNARK proves inclusion against that root.
 * See pool_native.tolk and depositFlow.ts for the trust model.
 */
export const MERKLE_TREE_HEIGHT = 20;
/** Number of historical roots kept on-chain so withdraws don't have to use the latest root. */
export const ROOT_HISTORY_SIZE = 30;
/**
 * BLS12-381 scalar field size (the `r` of the curve).
 * 0x73eda753299d7d483339d80809a1d80553bda402fffe5bfeffffffff00000001
 */
export const FIELD_SIZE_BLS12_381 = BigInt("52435875175126190479447740508185965837690552500527637822603658699938581184513");
/**
 * `keccak256("tornado") mod FIELD_SIZE_BLS12_381`. Used as the zero leaf of
 * the Merkle tree (and as the value at every empty subtree).
 *
 * Seed string is the original Tornado seed (`"tornado"`), not the project
 * name. The seed itself is not security-sensitive — only that this value is
 * deterministic, in-field, and consistent across every site that computes
 * an empty-subtree root.
 *
 * MUST match `ZERO_VALUE` in `pool_native.tolk` and the inlined constant in
 * `circuits/merkleUpdate.circom`. Changing it requires a fresh trusted
 * setup ceremony (new zkey → new verifier.tolk → new mainnet deploy).
 */
export const ZERO_VALUE = BigInt(
// keccak256("tornado") = 0xfeae3a2dffaaccd3a3a4b58f8ce9cf5cc5cb35e6f3e9b00f4ad8a3c34a07cb6e
// reduced mod the BLS12-381 scalar field:
"0x2fe54c60d3acabf3343a35b6eba15db4821b340f76e741e2249685ed4899af6c");
/** Op codes for internal messages between pool contracts and clients. */
export const OP_CODES = {
    /** User → pool. Deposit native TON with attached commitment. */
    DEPOSIT_NATIVE: 0xded05701,
    /** Jetton wallet → pool. transfer_notification (TEP-74). */
    JETTON_TRANSFER_NOTIFICATION: 0x7362d09c,
    /** Pool → its own jetton wallet. TEP-74 outgoing transfer (used on withdraw). */
    JETTON_TRANSFER: 0xf8a7ea5,
    /** Relayer → pool. Submit a withdrawal proof. */
    WITHDRAW: 0xded05702,
    /** Pool → owner. Pause/unpause/upgrade ops (admin). */
    ADMIN_PAUSE: 0xded057f1,
    ADMIN_UNPAUSE: 0xded057f2,
    /** Owner → native-pool. Recover stuck TON excess, capped on-chain so
     *  the admin cannot dip into depositor backing funds. */
    ADMIN_RECOVER_STUCK: 0xded057f3,
    /** Owner → jetton-pool. One-shot binder for pool's own jetton wallet. */
    ADMIN_BIND_JETTON_WALLET: 0xded057f4,
    /** Owner → jetton-pool. Emergency jetton recovery (no on-chain cap). */
    ADMIN_RECOVER_STUCK_JETTON: 0xded057f5,
    /** Out-message log topics (these are emitted as ext_out_msg bodies). */
    LOG_DEPOSIT: 0xded057a1,
    LOG_WITHDRAW: 0xded057a2,
};
/**
 * Note prefix. Format: `tonado-<asset>-<amount>-<network>-<hex>` where
 * `<hex>` is 0x-prefixed 62 bytes (31 bytes nullifier ‖ 31 bytes secret).
 *
 * `<asset>` is `ton` for native, or `jetton:<jetton-master-addr-base64url>`
 * for jettons.
 */
export const NOTE_PREFIX = "tonado";
/** Each of nullifier and secret is sampled as a 31-byte (248-bit) value. */
export const NULLIFIER_BYTES = 31;
export const SECRET_BYTES = 31;
/** TON nanoTON conversion. */
export const NANO_TON_PER_TON = 1000000000n;
//# sourceMappingURL=constants.js.map